import Button from "@/components/Button";
import CheckboxLabel from "@/components/CheckboxLabel";
import IconButton from "@/components/IconButton";
import { TextInput } from "@/components/InputFields";
import MaterialIcon from "@/components/MaterialIcon";
import Spinner from "@/components/Spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/chadcn/Dialog";
import {
  MEDICATION_TIMINGS,
  MEDICATION_TIMING_LABELS,
  Medication,
  MedicationApiDto,
  MedicationTiming,
} from "@/model/Medication";
import cn from "@/util/cn";
import { useForm, useStore } from "@tanstack/react-form";
import { isEqual } from "lodash";
import { useEffect, useRef } from "react";

type FormMedication = { uid: string; name: string; timing: MedicationTiming[] };
type FormValues = { medications: FormMedication[] };

type UpdateMedicationDialogProps = {
  medications: Medication[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isError: boolean;
  onSubmit: (medications: MedicationApiDto[]) => Promise<void>;
};

const toFormValues = (medications: Medication[]): FormValues => ({
  medications: medications.map(({ name, timing }, index) => ({
    uid: `existing-${index}`,
    name,
    timing: [...timing],
  })),
});

export const UpdateMedicationDialog = ({
  medications,
  open,
  onOpenChange,
  isError: isSubmitError,
  onSubmit,
}: UpdateMedicationDialogProps) => {
  const initialValues = toFormValues(medications);
  const newRowCounter = useRef(0);
  const wasOpen = useRef(false);

  const onSubmitInternal = async ({ value }: { value: FormValues }) =>
    await onSubmit(value.medications.map(({ name, timing }) => ({ name, timing })));

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: onSubmitInternal,
  });

  useEffect(() => {
    if (open && !wasOpen.current) {
      form.reset(toFormValues(medications));
    }
    wasOpen.current = open;
  }, [open, form, medications]);

  const currentMedications = useStore(form.store, ({ values }) => values.medications);

  const canSubmit = useStore(form.store, ({ values, isSubmitting }) => {
    const hasChanges = !isEqual(initialValues, values);
    const allValid = values.medications.every((medication) => medication.name.trim().length > 0);
    return hasChanges && allValid && !isSubmitting;
  });

  const initialByUid = new Map(initialValues.medications.map((it) => [it.uid, it]));
  const isRowEdited = (row: FormMedication) => {
    const original = initialByUid.get(row.uid);
    return !original || !isEqual(original, row);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-140 flex-col overflow-hidden bg-white">
        <DialogHeader className="mb-4">
          <DialogTitle className="mb-1 text-2xl font-bold">Update medication plan</DialogTitle>
          <DialogDescription className="text-sm text-primary/60">
            Please note, the current release will not notify the patient on what medication to take.
            Contact the patient directly to communicate any medication changes.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="flex min-h-0 flex-col gap-4 overflow-y-auto pe-1">
            <form.Field name="medications" mode="array">
              {(medicationsField) => (
                <>
                  {currentMedications.map((row, index) => {
                    return (
                      <div
                        key={row.uid}
                        className={cn(
                          "flex flex-col gap-3 rounded-md border p-3",
                          isRowEdited(row) ? "border-teal-200 bg-teal-50" : "border-gray-200"
                        )}
                      >
                        <div className="grid grid-cols-[1fr_auto] items-end gap-3">
                          <label className="flex flex-col gap-1 text-sm text-primary/60">
                            Medication
                            <form.Field name={`medications[${index}].name`}>
                              {(field) => (
                                <TextInput
                                  id={`medication-${index}-name`}
                                  className="py-1.5 text-primary/90"
                                  placeholder="Medication name"
                                  value={field.state.value}
                                  onChange={(e) => field.handleChange(e.target.value)}
                                />
                              )}
                            </form.Field>
                          </label>
                          <IconButton
                            type="button"
                            variant="danger"
                            className="mb-1"
                            onClick={() => medicationsField.removeValue(index)}
                          >
                            <MaterialIcon iconName="delete" style={{ fontSize: "20px" }} />
                          </IconButton>
                        </div>

                        <form.Field name={`medications[${index}].timing`}>
                          {(field) => {
                            const timing = field.state.value;
                            const toggleTiming = (value: MedicationTiming) =>
                              field.handleChange(
                                timing.includes(value)
                                  ? timing.filter((it) => it !== value)
                                  : [...timing, value]
                              );

                            return (
                              <div className="flex flex-wrap items-center gap-4">
                                <span className="text-sm text-primary/60">Timing</span>
                                {MEDICATION_TIMINGS.map((value) => (
                                  <CheckboxLabel
                                    key={value}
                                    label={MEDICATION_TIMING_LABELS[value]}
                                    checked={timing.includes(value)}
                                    onCheckedChange={() => toggleTiming(value)}
                                  />
                                ))}
                              </div>
                            );
                          }}
                        </form.Field>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() =>
                      medicationsField.pushValue({
                        uid: `new-${(newRowCounter.current += 1)}`,
                        name: "",
                        timing: [],
                      })
                    }
                    className="flex cursor-pointer items-center justify-center rounded-md border border-dashed border-teal-300 bg-teal-50/50 py-3 text-sm font-semibold text-primary/90 hover:bg-teal-50"
                  >
                    <MaterialIcon iconName="add" className="me-1" style={{ fontSize: "18px" }} />
                    Add Medication
                  </button>
                </>
              )}
            </form.Field>
          </div>
          {isSubmitError && (
            <p className="mt-4 text-red-600">
              There was an error saving the medication plan. Please contact Bloomlife Support, or
              try again later.
            </p>
          )}
          <form.Subscribe
            selector={(state) => state.isSubmitting}
            children={(isSubmitting) => (
              <DialogFooter className="mt-6 flex-row justify-end gap-2">
                <Button variant="neutral" type="button" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={!canSubmit}>
                  Save
                  {isSubmitting && <Spinner className="ms-2 size-4 border-2" />}
                </Button>
              </DialogFooter>
            )}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};
